import type {
  AddOrganizationMember,
  AddOrganizationMembersRequest,
  IdentityLookupResponse,
  LookedUpIdentity,
  OrganizationMemberRole,
  SsoDomainClaim,
} from '@/generated/http-clients/v3ApiSchemas'

type MemberRowBase = {
  key: string
  email: string
  role: OrganizationMemberRole
  lookupId: number
}

export type MemberRow = MemberRowBase &
  (
    | { status: 'pending' }
    | { status: 'lookupFailed' }
    | { status: 'alreadyMember' }
    | { status: 'ssoDomainNoIdentity'; ssoDomainClaim?: SsoDomainClaim }
    | { status: 'noSelectableIdentity' }
    | { status: 'new'; firstName: string; lastName: string }
    | { status: 'existing'; userId: string; identity: LookedUpIdentity }
    | {
        status: 'multiple'
        identities: LookedUpIdentity[]
        memberUserIds: readonly string[]
        selectedUserId?: string
      }
  )

const BLOCKED_STATUSES: ReadonlySet<MemberRow['status']> = new Set([
  'lookupFailed',
  'alreadyMember',
  'ssoDomainNoIdentity',
  'noSelectableIdentity',
])

const SENDABLE_STATUSES: ReadonlySet<MemberRow['status']> = new Set([
  'new',
  'existing',
  'multiple',
])

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

function memberUserIdsOf(
  lookup: IdentityLookupResponse,
  memberOrgIds: readonly string[],
): string[] {
  return lookup.identities
    .filter((identity) =>
      identity.organizations.some((organization) =>
        memberOrgIds.includes(organization.orgId),
      ),
    )
    .map((identity) => identity.userId)
}

function findIdentity(
  identities: LookedUpIdentity[],
  userId: string | undefined,
): LookedUpIdentity | undefined {
  if (userId === undefined) {
    return undefined
  }
  return identities.find((identity) => identity.userId === userId)
}

function everySelectableIsMember(
  identities: LookedUpIdentity[],
  memberUserIds: readonly string[],
): boolean {
  return identities
    .filter((identity) => identity.selectable)
    .every((identity) => memberUserIds.includes(identity.userId))
}

function pickerRow(
  base: MemberRowBase,
  identities: LookedUpIdentity[],
  suggested: LookedUpIdentity | undefined,
  memberUserIds: readonly string[],
): MemberRow {
  if (
    memberUserIds.length > 0 &&
    everySelectableIsMember(identities, memberUserIds)
  ) {
    return { ...base, status: 'alreadyMember' }
  }
  const selectedUserId =
    suggested && !memberUserIds.includes(suggested.userId)
      ? suggested.userId
      : undefined
  return {
    ...base,
    status: 'multiple',
    identities,
    memberUserIds,
    selectedUserId,
  }
}

export function rowFromLookup(
  row: MemberRow,
  lookup: IdentityLookupResponse,
  memberOrgIds: readonly string[],
): MemberRow {
  const base: MemberRowBase = {
    key: row.key,
    email: row.email,
    role: row.role,
    lookupId: row.lookupId,
  }

  const memberUserIds = memberUserIdsOf(lookup, memberOrgIds)
  const suggested = findIdentity(lookup.identities, lookup.suggestedUserId)

  switch (lookup.resolution) {
    case 'NONE':
      return { ...base, status: 'new', firstName: '', lastName: '' }
    case 'SINGLE':
      if (!suggested) {
        return pickerRow(base, lookup.identities, undefined, memberUserIds)
      }
      if (memberUserIds.includes(suggested.userId)) {
        return { ...base, status: 'alreadyMember' }
      }
      return {
        ...base,
        status: 'existing',
        userId: suggested.userId,
        identity: suggested,
      }
    case 'MULTIPLE':
      return pickerRow(base, lookup.identities, suggested, memberUserIds)
    case 'SSO_DOMAIN_NO_IDENTITY':
      if (memberUserIds.length > 0) {
        return { ...base, status: 'alreadyMember' }
      }
      return {
        ...base,
        status: 'ssoDomainNoIdentity',
        ssoDomainClaim: lookup.ssoDomainClaim,
      }
    case 'NO_SELECTABLE_IDENTITY':
      if (memberUserIds.length > 0) {
        return { ...base, status: 'alreadyMember' }
      }
      return { ...base, status: 'noSelectableIdentity' }
  }
}

export function isRowBlocked(row: MemberRow): boolean {
  return BLOCKED_STATUSES.has(row.status)
}

export function isRowSendable(row: MemberRow): boolean {
  return SENDABLE_STATUSES.has(row.status)
}

function isSelectedIdentityEligible(
  row: Extract<MemberRow, { status: 'multiple' }>,
): boolean {
  const selected = findIdentity(row.identities, row.selectedUserId)
  return (
    selected?.selectable === true &&
    !row.memberUserIds.includes(selected.userId)
  )
}

export function isRowComplete(row: MemberRow): boolean {
  switch (row.status) {
    case 'new':
      return row.firstName.trim() !== '' && row.lastName.trim() !== ''
    case 'existing':
      return true
    case 'multiple':
      return isSelectedIdentityEligible(row)
    default:
      return false
  }
}

export function canSubmitRows(rows: readonly MemberRow[]): boolean {
  const sendable = rows.filter(isRowSendable)
  return (
    sendable.length > 0 &&
    sendable.every(isRowComplete) &&
    rows.every((row) => row.status !== 'pending')
  )
}

function toMember(row: MemberRow): AddOrganizationMember | undefined {
  switch (row.status) {
    case 'new':
      return {
        email: row.email,
        role: row.role,
        firstName: row.firstName.trim(),
        lastName: row.lastName.trim(),
      }
    case 'existing':
      return { email: row.email, userId: row.userId, role: row.role }
    case 'multiple':
      return { email: row.email, userId: row.selectedUserId, role: row.role }
    default:
      return undefined
  }
}

export function buildAddMembersRequest(
  rows: readonly MemberRow[],
): AddOrganizationMembersRequest {
  const members: AddOrganizationMember[] = []
  for (const row of rows) {
    const member = toMember(row)
    if (member) {
      members.push(member)
    }
  }
  return { members }
}
